import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  ArrowRight,
  ArrowUpRight,
  Bookmark,
  ClipboardList,
  Minus,
  Plus,
  Radar,
  ShoppingBag,
  TrendingDown,
  TrendingUp,
  X,
} from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";

import BirdsEyeBoxSelectionModal from "./BirdsEyeBoxSelectionModal";
import BirdsEyeMapEmptyState, {
  type BirdsEyeMapEmptyReason,
} from "./BirdsEyeMapEmptyState";
import {
  ReachMapAccountHoverCard,
  RevenueMapAccountHoverCard,
  computeMapHoverPosition,
} from "./BirdsEyeMapHoverCards";
import {
  clusterFromOverall,
  healthDotColorFromOverall,
  overallScoreToReach01,
  quadrantFromReach,
} from "./birdsEyeMapStyles";
import SyntheticDataModeBanner from "../../../../../Components/SyntheticDataModeBanner";
import TabsComponent from "../../../../../Components/Tab";
import { FLOATING_SELECTION_BAR_PANEL } from "../../../../../intelligence-briefing-library/components/hawkeye/KpiCompareSelectionStrip";
import SpiderCompareModalContent from "../../../../../intelligence-briefing-library/components/hawkeye/SpiderCompareModalContent";
import {
  Dialog,
  DialogContent,
} from "../../../../../intelligence-briefing-library/components/ui/dialog";
import {
  useHawkeyeMetrics,
  type AccountMatrixData,
  type HawkeyeMetricsAccountsInput,
} from "../../../../../intelligence-briefing-library/hooks/useHawkeyeMetrics";
import { useAppDispatch, useAppSelector } from "../../../../../Store/Hooks";
import { fetchHealthScoreThresholds } from "../../../../../Store/Reducers/VendorAdminSlice";
import { DEFAULT_HEALTH_SCORE_THRESHOLDS } from "../../../../../Utils/HealthThresholds";
import { formatCompactCurrency } from "../../../../../Utils/Helper/formatCompactCurrency";
import CreateWatchlistModal, {
  type CreateWatchlistModalSuccess,
} from "../CreateWatchlistModal";

type BirdsEyeView = "reach" | "revenue";
type Quadrant = "expand" | "triage" | "defend" | "delegate";
export type BirdsEyeClusterId =
  | "renewal-risk"
  | "strong-performers"
  | "high-adoption-mixed"
  | "kpi-data-not-available";

/** Score-based map clusters only (excludes sidebar KPI-availability group). */
export type BirdsEyeMapClusterId = Exclude<
  BirdsEyeClusterId,
  "kpi-data-not-available"
>;

type ClusterId = BirdsEyeMapClusterId;

export type BirdsEyeClusterSummaryAccount = {
  accountId: string;
  accountName: string;
  overall: number;
};

export type BirdsEyeClusterSummary = {
  clusterId: BirdsEyeClusterId;
  label: string;
  accentColor: string;
  textColor: string;
  count: number;
  avg: number;
  accounts: BirdsEyeClusterSummaryAccount[];
};

const normalizeAccountIds = (accountIds: string[]) =>
  Array.from(new Set(accountIds)).sort((left, right) =>
    left.localeCompare(right)
  );

interface BirdsEyeOverviewProps {
  metricsAccountsInput?: HawkeyeMetricsAccountsInput;
  measurementDate?: string | null;
  onClusterGroupsChange?: (groups: BirdsEyeClusterSummary[]) => void;
  onSelectedAccountIdsChange?: (accountIds: string[]) => void;
  selectedAccountIds?: string[];
  onSaveAsWatchlist?: (accountIds: string[]) => void;
  onSaveAccountsSuccess?: (result: CreateWatchlistModalSuccess) => void;
}

type Point = {
  account_id: string;
  account_name: string;
  /** Growth opportunity 0–1 (maps to birdseye_reach.x). */
  reachOpp: number;
  /** Risk intensity 0–1; higher = more risk (maps to birdseye_reach.y, high at top of chart). */
  reachRisk: number;
  quadrant: Quadrant;
  cluster: ClusterId;
  overall: number;
  arr: number;
  /** ARR in millions for revenue chart. */
  arrM: number;
  nrr: number;
  execEngagement: number;
};

/** Latest plot geometry for drag–marquee selection (refs avoid stale closures). */
type BirdsEyeMarqueePickApi = {
  view: BirdsEyeView;
  points: Point[];
  reachCollisionOffsets: Map<string, { dx: number; dy: number }>;
  revenueCollisionOffsets: Map<string, { dx: number; dy: number }>;
  padL: number;
  padT: number;
  plotW: number;
  plotH: number;
  padForRev: { l: number; r: number; t: number; b: number };
  revPlotW: number;
  revPlotH: number;
  npxReach: (p: Point) => number;
  npyReach: (p: Point) => number;
  toRevX: (arrM: number) => number;
  toRevY: (nrr: number) => number;
};

/** Stack revenue bubbles with the same rounded ARR & NRR (no pixel jitter). */
const revenueStackKey = (p: Pick<Point, "arr" | "nrr">) =>
  `${Math.round(p.arr)}|${Math.round(p.nrr)}`;

const MIN_COMPARE_ACCOUNTS = 2;
const MAX_COMPARE_ACCOUNTS = 4;
/** Avatars shown in the floating bar before a +N overflow badge. */
const SELECTION_STRIP_MAX_AVATARS = 10;
const COMPARE_COLORS = ["#7c3aed", "#2563eb", "#0d9488", "#d97706"];
const REACH_POPUP_CARD_WIDTH = 288;
const REVENUE_POPUP_CARD_WIDTH = 268;
const REACH_POPUP_CARD_HEIGHT = 220;
const REVENUE_POPUP_CARD_HEIGHT = 240;
const REVENUE_POPUP_CARD_HEIGHT_WITH_PEERS = 320;
const MAP_POPUP_GAP_PX = 14;
const MAP_POPUP_EDGE_PX = 8;

/** Compact action buttons in the bottom selection bar. */
const SELECTION_BAR_OUTLINE_BTN =
  "inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-md border border-violet-600 bg-white px-2 py-1.5 text-[11px] font-semibold leading-tight text-violet-700 transition-colors hover:bg-violet-50";
const SELECTION_BAR_PRIMARY_BTN =
  "inline-flex shrink-0 items-center whitespace-nowrap rounded-md bg-violet-600 px-2 py-1.5 text-[11px] font-semibold leading-tight text-white transition-colors hover:bg-violet-700 disabled:cursor-not-allowed disabled:opacity-50";

const VIEW_PAIR: Array<{
  id: "reach" | "revenue";
  label: string;
  icon: React.ReactNode;
}> = [
  {
    id: "reach",
    label: "Risk-Opportunity Map",
    icon: <Radar size={13} strokeWidth={1.75} />,
  },
  {
    id: "revenue",
    label: "Revenue Map",
    icon: <TrendingUp size={13} strokeWidth={1.75} />,
  },
];

const QUADRANT_STYLES: Record<
  Quadrant,
  {
    label: string;
    color: string;
    text: string;
    fill: string;
    stripTitle: string;
    stripBlurb: string;
  }
> = {
  triage: {
    label: "Triage",
    color: "bg-rose-500",
    text: "text-rose-700",
    fill: "#ef4444",
    stripTitle: "Triage",
    stripBlurb: "High risk, low opportunity",
  },
  defend: {
    label: "Defend",
    color: "bg-orange-500",
    text: "text-orange-700",
    fill: "#f97316",
    stripTitle: "Defend",
    stripBlurb: "High risk, high opportunity",
  },
  delegate: {
    label: "Monitor",
    color: "bg-slate-400",
    text: "text-slate-700",
    fill: "#94a3b8",
    stripTitle: "Monitor",
    stripBlurb: "Low risk, low opportunity",
  },
  expand: {
    label: "Expand",
    color: "bg-emerald-500",
    text: "text-emerald-700",
    fill: "#16a34a",
    stripTitle: "Expand",
    stripBlurb: "Low risk, high opportunity",
  },
};

const QUADRANT_ACTIONS: Record<
  Quadrant,
  { border: string; titleColor: string; items: string[] }
> = {
  triage: {
    border: "#ef4444",
    titleColor: "#ef4444",
    items: [
      "Assign exec sponsor immediately",
      "Emergency account review within 7 days",
      "Activate churn prevention playbook",
    ],
  },
  defend: {
    border: "#f97316",
    titleColor: "#f97316",
    items: [
      "Prioritize exec-to-exec relationship",
      "Fast-track open escalations",
      "Lock renewal before competitor entry",
    ],
  },
  delegate: {
    border: "#94a3b8",
    titleColor: "#64748b",
    items: [
      "Automate health touchpoints",
      "Quarterly review cycle sufficient",
      "Monitor for expansion signals",
    ],
  },
  expand: {
    border: "#22c55e",
    titleColor: "#16a34a",
    items: [
      "Open expansion conversation now",
      "Nominate for reference / case study",
      "Leverage exec network introductions",
    ],
  },
};

const CLUSTER_META: Record<
  ClusterId,
  { label: string; color: string; bg: string; text: string; short: string }
> = {
  "renewal-risk": {
    label: "Renewal Risk",
    short: "RR",
    color: "#ef4444",
    bg: "#fee2e2",
    text: "#dc2626",
  },
  "strong-performers": {
    label: "Strong Performers",
    short: "SP",
    color: "#22c55e",
    bg: "#dcfce7",
    text: "#16a34a",
  },
  "high-adoption-mixed": {
    label: "High Adoption / Mixed",
    short: "HA",
    color: "#eab308",
    bg: "#fefce8",
    text: "rgb(209 160 7)",
  },
};

const KPI_DATA_NOT_AVAILABLE_META = {
  label: "KPIs Data not available",
  short: "NA",
  color: "#94a3b8",
  bg: "#e2e8f0",
  text: "#64748b",
} as const;

/** Opaque fills so stacked/overlapping nodes stay readable (no see-through tint). */
const NODE_FILL_MAP: Record<ClusterId, string> = {
  "renewal-risk": CLUSTER_META["renewal-risk"].bg,
  "strong-performers": CLUSTER_META["strong-performers"].bg,
  "high-adoption-mixed": CLUSTER_META["high-adoption-mixed"].bg,
};
const NODE_FILL_FALLBACK = "#f3f4f6";

const clamp = (n: number, min = 0, max = 100) =>
  Math.max(min, Math.min(max, n));

const initials = (name: string) =>
  (name || "")
    .split(" ")
    .filter(Boolean)
    .map(w => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

/** Single ARR headline in millions (no artificial lo–hi band). */
const formatArrRevenueBand = (arrM: number): string => {
  if (!Number.isFinite(arrM) || arrM <= 0) return "—";
  if (arrM < 0.5) return "<$0.5M";
  if (arrM < 1) return "<$1M";
  return formatCompactCurrency(arrM, { inputScale: "millions", fallback: "—" });
};

const reachMapSubtitle = (
  p: Point,
  accountData?: AccountMatrixData | null
): string => {
  const geo = [accountData?.region, accountData?.country]
    .filter(Boolean)
    .join(" · ");
  const band = formatArrRevenueBand(p.arrM);
  const parts = [geo, band].filter(s => s && s !== "—");
  return parts.join(" · ") || band;
};

/** Second line under account name on revenue click panel (geo / org; no duplicate ARR). */
const revenueMapPanelSubtitle = (
  accountData: AccountMatrixData | null | undefined,
  clusterLabel: string
): string => {
  const org = accountData?.digestMeta?.organization_name?.trim();
  if (org) return org;
  const geo = [accountData?.region, accountData?.country]
    .filter(Boolean)
    .join(" · ");
  if (geo) return geo;
  return clusterLabel;
};

const scoreTrajectoryLabel = (overall: number): string => {
  if (overall >= 75) return "Improving";
  if (overall >= 50) return "Stable";
  return "Declining";
};

const getExecAttentionTier = (
  eng: number,
  caution: number,
  critical: number
): "critical" | "caution" | "none" => {
  if (eng < critical) return "critical";
  if (eng < caution) return "caution";
  return "none";
};

const pickCategoryScore = (
  categoryScores: Array<{ category: string; score: number }>,
  categoryIds: string[]
) => {
  for (const id of categoryIds) {
    const found = categoryScores.find(c => c.category === id);
    if (found && typeof found.score === "number") return found.score;
  }
  return 0;
};

const toNum = (v: unknown) => {
  if (typeof v === "number" && Number.isFinite(v)) return v;
  if (typeof v === "string") {
    const n = parseFloat(v.replace(/,/g, ""));
    if (Number.isFinite(n)) return n;
  }
  return undefined;
};

/** Business Health dot color — thresholds from Settings when provided. */
const hColor = (
  overall: number,
  thresholds?: Parameters<typeof healthDotColorFromOverall>[1]
) => healthDotColorFromOverall(overall, thresholds);

type CollisionOffset = {
  dx: number;
  dy: number;
};

/**
 * Visual-only de-overlap: when multiple accounts render at same pixel,
 * fan them out deterministically while preserving true underlying values.
 */
function buildCollisionOffsets<T extends { account_id: string }>(
  points: T[],
  getX: (p: T) => number,
  getY: (p: T) => number,
  baseRadiusPx: number
): Map<string, CollisionOffset> {
  const byPixel = new Map<string, T[]>();

  points.forEach(p => {
    const px = Math.round(getX(p));
    const py = Math.round(getY(p));
    const key = `${px}:${py}`;
    if (!byPixel.has(key)) byPixel.set(key, []);
    byPixel.get(key)!.push(p);
  });

  const offsets = new Map<string, CollisionOffset>();
  byPixel.forEach(group => {
    const sorted = [...group].sort((a, b) =>
      a.account_id.localeCompare(b.account_id)
    );
    const count = sorted.length;
    if (count === 1) {
      offsets.set(sorted[0].account_id, { dx: 0, dy: 0 });
      return;
    }

    sorted.forEach((p, idx) => {
      const angle = (2 * Math.PI * idx) / count;
      const ring = Math.floor(idx / 8);
      const radius = baseRadiusPx + ring * 6;
      offsets.set(p.account_id, {
        dx: Math.cos(angle) * radius,
        dy: Math.sin(angle) * radius,
      });
    });
  });

  return offsets;
}

/**
 * Revenue Y (broken axis): plot bottom anchors the 0–80% strip (no 0% tick label); values 0–80% map in a short lower strip;
 * 80%–130% is linear in the main area with 5% ticks (80, 85, …, 130) like the reference UI.
 */
const REVENUE_NRR_BREAK_FRAC = 0.1;

/** Revenue map bubble radius (kept as the visual reference size). */
const REVENUE_NODE_R = 13;
/** Reach map uses a smaller SVG viewBox, so reduce radius to match Revenue visually. */
const REACH_NODE_R = 8;
/** Gap between bubble edge and purple dashed selection ring. */
const SELECTION_RING_GAP = 3;

/** Marquee (drag) selection: circle–rectangle intersection for bubble hit-testing. */
function circleIntersectsRect(
  cx: number,
  cy: number,
  r: number,
  rx: number,
  ry: number,
  rw: number,
  rh: number
): boolean {
  const nx = Math.max(rx, Math.min(cx, rx + rw));
  const ny = Math.max(ry, Math.min(cy, ry + rh));
  const dx = cx - nx;
  const dy = cy - ny;
  return dx * dx + dy * dy <= r * r;
}

function clientToSvgPoint(
  svg: SVGSVGElement,
  clientX: number,
  clientY: number
): { x: number; y: number } {
  const rect = svg.getBoundingClientRect();
  const vb = svg.viewBox.baseVal;
  const sx = vb.width / Math.max(rect.width, 1);
  const sy = vb.height / Math.max(rect.height, 1);
  return {
    x: (clientX - rect.left) * sx + vb.x,
    y: (clientY - rect.top) * sy + vb.y,
  };
}

const MARQUEE_MIN_DRAG_CLIENT_PX = 5;
const REVENUE_COLLISION_BASE_PX = 10;
const REACH_COLLISION_BASE_PX = 12;

function chooseNiceStep(maxValue: number, targetTicks = 8): number {
  if (!Number.isFinite(maxValue) || maxValue <= 0) return 1;
  const rough = maxValue / Math.max(1, targetTicks);
  const power = Math.pow(10, Math.floor(Math.log10(rough)));
  const norm = rough / power;
  const nice =
    norm <= 1 ? 1 : norm <= 2 ? 2 : norm <= 2.5 ? 2.5 : norm <= 5 ? 5 : 10;
  return nice * power;
}

function pickExecEngagement(
  rawMetrics: Array<{
    metric_id: string;
    normalized_value?: number;
    value?: string;
  }>,
  engagementCategory: number
): number {
  const prefer = [
    "executive_engagement",
    "relationship_health",
    "customer_responsiveness",
    "active_users",
  ];
  for (const id of prefer) {
    const m = rawMetrics.find(r => r.metric_id === id);
    if (m) {
      if (
        typeof m.normalized_value === "number" &&
        Number.isFinite(m.normalized_value)
      ) {
        return clamp(m.normalized_value, 0, 100);
      }
      const n = toNum(m.value);
      if (n !== undefined) return clamp(n, 0, 100);
    }
  }
  return clamp(engagementCategory, 0, 100);
}

const BirdsEyeOverview: React.FC<BirdsEyeOverviewProps> = ({
  metricsAccountsInput,
  measurementDate,
  onClusterGroupsChange,
  onSelectedAccountIdsChange,
  selectedAccountIds = [],
  onSaveAsWatchlist,
  onSaveAccountsSuccess,
}) => {
  const dispatch = useAppDispatch();
  const healthScoreThresholds = useAppSelector(
    state => state.vendorAdmin.healthScoreThresholds
  );
  const bhThresholds =
    healthScoreThresholds?.business_health ??
    DEFAULT_HEALTH_SCORE_THRESHOLDS.business_health;
  const ahThresholds =
    healthScoreThresholds?.account_health ??
    DEFAULT_HEALTH_SCORE_THRESHOLDS.account_health;

  useEffect(() => {
    void dispatch(fetchHealthScoreThresholds());
  }, [dispatch]);

  const [view, setView] = useState<BirdsEyeView>("reach");
  /** Shared across Risk–Opportunity and Revenue maps. */
  const [selectedCluster, setSelectedCluster] = useState<ClusterId | null>(
    null
  );
  const [internalSelectedAccountIds, setInternalSelectedAccountIds] = useState<
    string[]
  >([]);
  /** Up to 4 accounts in the strip / spider compare (subset of map selection). */
  const [compareSlotIds, setCompareSlotIds] = useState<string[]>([]);
  const [isCompareModalOpen, setIsCompareModalOpen] = useState(false);
  const [isSaveWatchlistModalOpen, setIsSaveWatchlistModalOpen] =
    useState(false);
  const [compareModalSelectedIds, setCompareModalSelectedIds] = useState<
    string[]
  >([]);
  const [compareSelectionMessage, setCompareSelectionMessage] = useState<
    string | null
  >(null);
  const [popupAccountId, setPopupAccountId] = useState<string | null>(null);
  const [popupCardPos, setPopupCardPos] = useState<{
    left: number;
    top: number;
  } | null>(null);
  /** Revenue: account ids sharing the same rounded ARR & NRR (stacked bubble). */
  const [revenuePopupPeers, setRevenuePopupPeers] = useState<string[] | null>(
    null
  );
  /** Accounts captured by the last non-additive box-select (overlap resolver modal). */
  const [boxPickModalIds, setBoxPickModalIds] = useState<string[] | null>(null);
  /** Selection inside the snippet modal only (not synced to the main map on open). */
  const [boxModalSelectedAccountIds, setBoxModalSelectedAccountIds] = useState<
    string[]
  >([]);
  const [watchlistSaveSeedIds, setWatchlistSaveSeedIds] = useState<
    string[] | null
  >(null);

  /** Risk–Opportunity map: hover = compact insight; click = full account card. */
  const [reachHoverAccountId, setReachHoverAccountId] = useState<string | null>(
    null
  );
  const [reachHoverPos, setReachHoverPos] = useState<{ x: number; y: number }>({
    x: 0,
    y: 0,
  });
  const reachHoverLeaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const [revenueHoverAccountId, setRevenueHoverAccountId] = useState<
    string | null
  >(null);
  const [revenueHoverPos, setRevenueHoverPos] = useState<{
    x: number;
    y: number;
  }>({ x: 0, y: 0 });
  const revenueHoverLeaveTimerRef = useRef<ReturnType<
    typeof setTimeout
  > | null>(null);
  const normalizedSelectedAccountIds = useMemo(
    () =>
      normalizeAccountIds(
        onSelectedAccountIdsChange
          ? selectedAccountIds
          : internalSelectedAccountIds
      ),
    [internalSelectedAccountIds, onSelectedAccountIdsChange, selectedAccountIds]
  );
  const selectedIds = useMemo(
    () => new Set(normalizedSelectedAccountIds),
    [normalizedSelectedAccountIds]
  );

  /** Tracks card bounds so the selection bar stays visible while scrolling. */
  const panelBoundsRef = useRef<HTMLDivElement>(null);
  const [selectionBarDock, setSelectionBarDock] = useState<{
    left: number;
    width: number;
    bottom: number;
  } | null>(null);
  const hasSelectionRef = useRef(false);
  hasSelectionRef.current = selectedIds.size > 0;

  const syncSelectionBarDock = useCallback(() => {
    if (!hasSelectionRef.current) return;
    const el = panelBoundsRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const insetX = 16;
    const bottomGap = 12;
    const nextDock = {
      left: Math.round(rect.left + insetX),
      width: Math.round(Math.max(240, rect.width - insetX * 2)),
      bottom: Math.round(
        Math.max(bottomGap, window.innerHeight - rect.bottom + bottomGap)
      ),
    };

    setSelectionBarDock(prev => {
      if (
        prev &&
        prev.left === nextDock.left &&
        prev.width === nextDock.width &&
        prev.bottom === nextDock.bottom
      ) {
        return prev;
      }
      return nextDock;
    });
  }, []);

  useEffect(() => {
    if (selectedIds.size === 0) {
      setSelectionBarDock(prev => (prev === null ? prev : null));
      return;
    }
    syncSelectionBarDock();
    const el = panelBoundsRef.current;
    const ro = new ResizeObserver(() => syncSelectionBarDock());
    if (el) ro.observe(el);
    window.addEventListener("resize", syncSelectionBarDock);
    window.addEventListener("scroll", syncSelectionBarDock, true);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", syncSelectionBarDock);
      window.removeEventListener("scroll", syncSelectionBarDock, true);
    };
  }, [selectedIds.size, syncSelectionBarDock]);

  /** SVG that hosts the active map (only one of reach/revenue is mounted). */
  const plotSvgRef = useRef<SVGSVGElement | null>(null);
  const plotContainerRef = useRef<HTMLDivElement | null>(null);
  const marqueePickRef = useRef<BirdsEyeMarqueePickApi | null>(null);
  const marqueeSessionRef = useRef<{
    startClientX: number;
    startClientY: number;
    startSvgX: number;
    startSvgY: number;
    pointerId: number;
    additive: boolean;
  } | null>(null);
  const [marqueeBox, setMarqueeBox] = useState<{
    x: number;
    y: number;
    width: number;
    height: number;
  } | null>(null);

  const clearReachHoverLeaveTimer = useCallback(() => {
    if (reachHoverLeaveTimerRef.current) {
      clearTimeout(reachHoverLeaveTimerRef.current);
      reachHoverLeaveTimerRef.current = null;
    }
  }, []);

  const scheduleReachHoverHide = useCallback(() => {
    clearReachHoverLeaveTimer();
    reachHoverLeaveTimerRef.current = setTimeout(() => {
      setReachHoverAccountId(null);
      reachHoverLeaveTimerRef.current = null;
    }, 140);
  }, [clearReachHoverLeaveTimer]);

  const clearRevenueHoverLeaveTimer = useCallback(() => {
    if (revenueHoverLeaveTimerRef.current) {
      clearTimeout(revenueHoverLeaveTimerRef.current);
      revenueHoverLeaveTimerRef.current = null;
    }
  }, []);

  const scheduleRevenueHoverHide = useCallback(() => {
    clearRevenueHoverLeaveTimer();
    revenueHoverLeaveTimerRef.current = setTimeout(() => {
      setRevenueHoverAccountId(null);
      revenueHoverLeaveTimerRef.current = null;
    }, 140);
  }, [clearRevenueHoverLeaveTimer]);

  useEffect(
    () => () => {
      clearReachHoverLeaveTimer();
      clearRevenueHoverLeaveTimer();
    },
    [clearReachHoverLeaveTimer, clearRevenueHoverLeaveTimer]
  );

  const applySelectedAccountIds = useCallback(
    (accountIds: Iterable<string>) => {
      const normalizedIds = normalizeAccountIds(Array.from(accountIds));
      if (onSelectedAccountIdsChange) {
        onSelectedAccountIdsChange(normalizedIds);
        return;
      }
      setInternalSelectedAccountIds(normalizedIds);
    },
    [onSelectedAccountIdsChange]
  );

  const updateSelectedAccountIds = useCallback(
    (updater: (prev: Set<string>) => Set<string>) => {
      const nextSelectedIds = updater(new Set(normalizedSelectedAccountIds));
      applySelectedAccountIds(nextSelectedIds);
    },
    [applySelectedAccountIds, normalizedSelectedAccountIds]
  );

  useEffect(() => {
    if (view !== "reach") {
      clearReachHoverLeaveTimer();
      setReachHoverAccountId(null);
    }
    if (view !== "revenue") {
      clearRevenueHoverLeaveTimer();
      setRevenueHoverAccountId(null);
    }
  }, [view, clearReachHoverLeaveTimer, clearRevenueHoverLeaveTimer]);

  const closeNodePopup = () => {
    setPopupAccountId(null);
    setPopupCardPos(null);
    setRevenuePopupPeers(null);
  };

  const positionPopupNearNode = useCallback(
    (
      nodeElement: SVGGElement | null,
      kind: "reach" | "revenue",
      peerCount = 1
    ) => {
      const container = plotContainerRef.current;
      if (!container || !nodeElement) {
        setPopupCardPos(null);
        return;
      }

      const containerRect = container.getBoundingClientRect();
      const nodeRect = nodeElement.getBoundingClientRect();
      const anchorX = nodeRect.left - containerRect.left + nodeRect.width / 2;
      const anchorY = nodeRect.top - containerRect.top + nodeRect.height / 2;
      const cardWidth =
        kind === "reach" ? REACH_POPUP_CARD_WIDTH : REVENUE_POPUP_CARD_WIDTH;
      const cardHeight =
        kind === "reach"
          ? REACH_POPUP_CARD_HEIGHT
          : peerCount > 1
            ? REVENUE_POPUP_CARD_HEIGHT_WITH_PEERS
            : REVENUE_POPUP_CARD_HEIGHT;

      const canPlaceRight =
        anchorX + MAP_POPUP_GAP_PX + cardWidth <=
        containerRect.width - MAP_POPUP_EDGE_PX;
      const rawLeft = canPlaceRight
        ? anchorX + MAP_POPUP_GAP_PX
        : anchorX - cardWidth - MAP_POPUP_GAP_PX;
      const rawTop = anchorY - cardHeight / 2;

      setPopupCardPos({
        left: clamp(
          rawLeft,
          MAP_POPUP_EDGE_PX,
          Math.max(
            MAP_POPUP_EDGE_PX,
            containerRect.width - cardWidth - MAP_POPUP_EDGE_PX
          )
        ),
        top: clamp(
          rawTop,
          MAP_POPUP_EDGE_PX,
          Math.max(
            MAP_POPUP_EDGE_PX,
            containerRect.height - cardHeight - MAP_POPUP_EDGE_PX
          )
        ),
      });
    },
    []
  );
  const [quadrantStripOpen, setQuadrantStripOpen] = useState(true);
  const execAttnCaution = 50;
  const execAttnCritical = 25;
  const { matrixData, loading, error, configuration, orderedCategories } =
    useHawkeyeMetrics(
      metricsAccountsInput,
      undefined,
      measurementDate ?? null,
      { requireDate: false }
    );

  const digestAccounts = useMemo(
    () => (matrixData || []).filter(a => a.hasDigest),
    [matrixData]
  );

  const digestCompareIdSet = useMemo(
    () => new Set(digestAccounts.map(a => a.account_id)),
    [digestAccounts]
  );

  const compareSpiderAccounts = useMemo(
    () =>
      digestAccounts.map(a => ({
        account_id: a.account_id,
        account_name: a.account_name,
        overallScore: a.overallScore,
        categoryScores: a.categoryScores.map(c => ({
          category: c.category,
          score: c.score,
        })),
      })),
    [digestAccounts]
  );

  const compareCategoryOrder = useMemo(() => {
    if (orderedCategories?.length) return orderedCategories;
    const inc = configuration?.includedCategories;
    if (inc?.length) return inc;
    const first = digestAccounts.find(x => x.categoryScores?.length);
    return first?.categoryScores.map(c => c.category) ?? [];
  }, [orderedCategories, configuration, digestAccounts]);

  useEffect(() => {
    if (!isCompareModalOpen) {
      setCompareSelectionMessage(null);
      return;
    }
    const n = compareModalSelectedIds.length;
    if (n === 0) {
      setCompareSelectionMessage(null);
      return;
    }
    if (n < MIN_COMPARE_ACCOUNTS) {
      setCompareSelectionMessage("Select at least 2 accounts to compare.");
      return;
    }
    if (n > MAX_COMPARE_ACCOUNTS) {
      setCompareSelectionMessage("Maximum 4 accounts can be compared.");
      return;
    }
    setCompareSelectionMessage(null);
  }, [isCompareModalOpen, compareModalSelectedIds.length]);

  const toggleCompareModalSelection = useCallback(
    (accountId: string) => {
      setCompareModalSelectedIds(prev => {
        if (prev.includes(accountId)) {
          if (isCompareModalOpen && prev.length <= MIN_COMPARE_ACCOUNTS) {
            setCompareSelectionMessage(
              "At least 2 accounts are required in comparison."
            );
            return prev;
          }
          return prev.filter(id => id !== accountId);
        }
        if (prev.length >= MAX_COMPARE_ACCOUNTS) {
          setCompareSelectionMessage("You can compare up to 4 accounts only.");
          return prev;
        }
        return [...prev, accountId];
      });
    },
    [isCompareModalOpen]
  );

  const handleCompareModalOpenChange = useCallback(
    (open: boolean) => {
      setIsCompareModalOpen(open);
      if (!open) {
        setCompareSlotIds(
          compareModalSelectedIds.slice(0, MAX_COMPARE_ACCOUNTS)
        );
        setCompareModalSelectedIds([]);
        setCompareSelectionMessage(null);
      }
    },
    [compareModalSelectedIds]
  );

  const handleOpenAccountCompare = useCallback(() => {
    setCompareModalSelectedIds(compareSlotIds.slice(0, MAX_COMPARE_ACCOUNTS));
    setCompareSelectionMessage(null);
    setIsCompareModalOpen(true);
    setBoxPickModalIds(null);
  }, [compareSlotIds]);

  const points = useMemo<Point[]>(() => {
    return digestAccounts.map(a => {
      const engagement = pickCategoryScore(a.categoryScores, ["engagement"]);
      const overall = clamp(a.overallScore);
      const { reachOpp, reachRisk } = overallScoreToReach01(
        overall,
        a.account_id,
        ahThresholds
      );
      const quadrant = quadrantFromReach(reachOpp, reachRisk);

      // Revenue map must use digest `_input_metrics[].current_value` for ARR/NRR.
      // In `useHawkeyeMetrics`, digest metric `value` is mapped from `current_value`.
      const arrMetric = a.rawMetrics.find(
        m =>
          m.metric_id === "arr" && (m.category_id || m.category) === "financial"
      );
      const nrrMetric = a.rawMetrics.find(
        m =>
          m.metric_id === "net_retention" &&
          (m.category_id || m.category) === "financial"
      );
      const arrRaw = toNum(arrMetric?.value) ?? 0;
      const arrM = arrRaw / 1_000_000;
      const nrrRaw = toNum(nrrMetric?.value) ?? 0;

      const cluster: ClusterId = clusterFromOverall(overall, bhThresholds);

      return {
        account_id: a.account_id,
        account_name: a.account_name,
        reachOpp,
        reachRisk,
        quadrant,
        cluster,
        overall,
        arr: arrRaw,
        arrM,
        nrr: nrrRaw,
        execEngagement: pickExecEngagement(a.rawMetrics, engagement),
      };
    });
  }, [digestAccounts, ahThresholds, bhThresholds]);

  const visiblePoints = useMemo(
    () =>
      selectedCluster
        ? points.filter(p => p.cluster === selectedCluster)
        : points,
    [points, selectedCluster]
  );

  const quadrantCounts = useMemo(
    () =>
      visiblePoints.reduce(
        (acc, p) => {
          acc[p.quadrant] += 1;
          return acc;
        },
        { expand: 0, triage: 0, defend: 0, delegate: 0 } as Record<
          Quadrant,
          number
        >
      ),
    [visiblePoints]
  );

  const selectedAccounts = useMemo(
    () => points.filter(p => selectedIds.has(p.account_id)),
    [points, selectedIds]
  );

  const pointsById = useMemo(
    () => new Map(points.map(point => [point.account_id, point])),
    [points]
  );

  const selectionStripVisibleAccounts = useMemo(
    () => selectedAccounts.slice(0, SELECTION_STRIP_MAX_AVATARS),
    [selectedAccounts]
  );

  const selectionStripOverflowCount = Math.max(
    0,
    selectedAccounts.length - selectionStripVisibleAccounts.length
  );

  const handleOpenSaveWatchlist = useCallback(() => {
    const accountIds = selectedAccounts.map(account => account.account_id);
    if (accountIds.length === 0) return;

    if (onSaveAsWatchlist) {
      onSaveAsWatchlist(accountIds);
      return;
    }

    setWatchlistSaveSeedIds(null);
    setIsSaveWatchlistModalOpen(true);
  }, [onSaveAsWatchlist, selectedAccounts]);

  /** Keep spider compare slots in sync; cap at 4 when map selection is larger. */
  useEffect(() => {
    const ordered = points
      .filter(p => selectedIds.has(p.account_id))
      .map(p => p.account_id);

    setCompareSlotIds(prev => {
      const kept = prev.filter(id => selectedIds.has(id));
      if (ordered.length === 0) return [];
      if (ordered.length <= MAX_COMPARE_ACCOUNTS) return ordered;
      if (kept.length >= MAX_COMPARE_ACCOUNTS) {
        return kept.slice(0, MAX_COMPARE_ACCOUNTS);
      }
      if (kept.length === 0) {
        return ordered.slice(0, MAX_COMPARE_ACCOUNTS);
      }
      return kept;
    });
  }, [selectedIds, points]);

  const watchlistInitialSelectedIds = useMemo(
    () =>
      watchlistSaveSeedIds ??
      selectedAccounts.map(account => account.account_id),
    [selectedAccounts, watchlistSaveSeedIds]
  );

  const watchlistPickerAccounts = useMemo(() => {
    const matrixById = new Map(
      (matrixData || []).map(row => [row.account_id, row])
    );
    const accountsForPicker =
      watchlistSaveSeedIds != null
        ? points.filter(p => watchlistSaveSeedIds.includes(p.account_id))
        : selectedAccounts;
    return accountsForPicker.map(account => {
      const row = matrixById.get(account.account_id);
      const location = row
        ? [row.city, row.region, row.country].filter(Boolean).join(", ")
        : "";
      const arrLabel = formatArrRevenueBand(account.arrM);
      return {
        id: account.account_id,
        name: account.account_name,
        initials: initials(account.account_name),
        location: location || "N/A",
        revenue: arrLabel === "—" ? "N/A" : arrLabel,
        tier: "N/A",
      };
    });
  }, [matrixData, points, selectedAccounts, watchlistSaveSeedIds]);

  const canOpenAccountCompare =
    compareSlotIds.length >= MIN_COMPARE_ACCOUNTS &&
    compareCategoryOrder.length > 0 &&
    digestCompareIdSet.size > 0 &&
    compareSlotIds.every(id => digestCompareIdSet.has(id));

  const compareButtonTitle = useMemo(() => {
    if (canOpenAccountCompare) {
      return "Open account comparison (spider view)";
    }
    const n = compareSlotIds.length;
    if (n < MIN_COMPARE_ACCOUNTS) {
      return `Select at least ${MIN_COMPARE_ACCOUNTS} accounts in the bar for spider view`;
    }
    if (
      digestCompareIdSet.size === 0 ||
      !compareSlotIds.every(id => digestCompareIdSet.has(id))
    ) {
      return "Selected accounts need digest data to compare";
    }
    return "Comparison is not available for the current data";
  }, [canOpenAccountCompare, compareSlotIds, digestCompareIdSet]);

  const revenueBuckets = useMemo(() => {
    const m = new Map<string, Point[]>();
    for (const p of visiblePoints) {
      const k = revenueStackKey(p);
      if (!m.has(k)) m.set(k, []);
      m.get(k)!.push(p);
    }
    return m;
  }, [visiblePoints]);

  const popupPoint = useMemo(
    () => visiblePoints.find(p => p.account_id === popupAccountId) || null,
    [visiblePoints, popupAccountId]
  );
  const popupAccountData = useMemo(
    () => matrixData.find(a => a.account_id === popupAccountId) || null,
    [matrixData, popupAccountId]
  );

  const reachHoverPoint = useMemo(
    () =>
      reachHoverAccountId
        ? visiblePoints.find(p => p.account_id === reachHoverAccountId) || null
        : null,
    [visiblePoints, reachHoverAccountId]
  );
  const reachHoverFixedPosition = useMemo(() => {
    if (!reachHoverPoint) return null;
    return computeMapHoverPosition(
      reachHoverPos.x,
      reachHoverPos.y,
      REACH_POPUP_CARD_HEIGHT
    );
  }, [reachHoverPoint, reachHoverPos.x, reachHoverPos.y]);

  const revenueHoverPoint = useMemo(
    () =>
      revenueHoverAccountId
        ? visiblePoints.find(p => p.account_id === revenueHoverAccountId) ||
          null
        : null,
    [visiblePoints, revenueHoverAccountId]
  );

  const revenueHoverFixedPosition = useMemo(() => {
    if (!revenueHoverPoint) return null;
    return computeMapHoverPosition(revenueHoverPos.x, revenueHoverPos.y, 168);
  }, [revenueHoverPoint, revenueHoverPos.x, revenueHoverPos.y]);

  const totalSelectedAccountCount =
    metricsAccountsInput?.selectedAccounts?.length ?? 0;

  const mapEmptyReason = useMemo((): BirdsEyeMapEmptyReason | null => {
    if (selectedCluster && visiblePoints.length === 0 && points.length > 0) {
      return "cluster-filter";
    }
    if (totalSelectedAccountCount === 0) return "no-accounts";
    if (points.length === 0) return "no-digest";
    return null;
  }, [
    selectedCluster,
    visiblePoints.length,
    points.length,
    totalSelectedAccountCount,
  ]);

  const showMapEmpty = visiblePoints.length === 0 && mapEmptyReason !== null;

  const clusterGroups = useMemo(() => {
    const noKpiIdSet = new Set(
      (matrixData || [])
        .filter(account => account.hasData === false)
        .map(account => account.account_id)
    );

    const sortAccounts = (left: Point, right: Point) =>
      left.overall - right.overall ||
      left.account_name.localeCompare(right.account_name);

    const scoreGroups = (Object.keys(CLUSTER_META) as ClusterId[]).map(
      cluster => {
        const accounts = points
          .filter(p => p.cluster === cluster && !noKpiIdSet.has(p.account_id))
          .sort(sortAccounts);
        const avg = accounts.length
          ? Math.round(
              accounts.reduce((sum, p) => sum + p.overall, 0) / accounts.length
            )
          : 0;
        return { cluster, accounts, avg };
      }
    );

    const pointById = new Map(points.map(point => [point.account_id, point]));
    const noKpiAccounts = (matrixData || [])
      .filter(account => account.hasData === false)
      .map(account => {
        const point = pointById.get(account.account_id);
        return (
          point ??
          ({
            account_id: account.account_id,
            account_name: account.account_name,
            reachOpp: 0,
            reachRisk: 0,
            quadrant: "delegate" as const,
            cluster: "high-adoption-mixed" as ClusterId,
            overall: clamp(account.overallScore),
            arr: 0,
            arrM: 0,
            nrr: 0,
            execEngagement: 0,
          } satisfies Point)
        );
      })
      .sort(sortAccounts);
    const noKpiAvg = noKpiAccounts.length
      ? Math.round(
          noKpiAccounts.reduce((sum, p) => sum + p.overall, 0) /
            noKpiAccounts.length
        )
      : 0;

    return [
      ...scoreGroups,
      {
        cluster: "kpi-data-not-available" as const,
        accounts: noKpiAccounts,
        avg: noKpiAvg,
      },
    ];
  }, [matrixData, points]);

  useEffect(() => {
    if (!onClusterGroupsChange) {
      return;
    }

    const toSummary = ({
      cluster,
      accounts,
      avg,
    }: {
      cluster: BirdsEyeClusterId;
      accounts: Point[];
      avg: number;
    }): BirdsEyeClusterSummary => {
      const meta =
        cluster === "kpi-data-not-available"
          ? KPI_DATA_NOT_AVAILABLE_META
          : CLUSTER_META[cluster as ClusterId];
      return {
        clusterId: cluster,
        label: meta.label,
        accentColor: meta.color,
        textColor: meta.text,
        count: accounts.length,
        avg,
        accounts: accounts.map(account => ({
          accountId: account.account_id,
          accountName: account.account_name,
          overall: account.overall,
        })),
      };
    };

    // While Bird's Eye metrics load, clear sidebar groups so Watchlist shows a
    // flat account list (not a premature "KPIs Data Not Available" dump).
    if (loading) {
      onClusterGroupsChange([]);
      return;
    }

    onClusterGroupsChange(clusterGroups.map(toSummary));
  }, [clusterGroups, loading, onClusterGroupsChange]);

  const arrMedianM = useMemo(() => {
    if (!points.length) return 0;
    const sorted = [...points].map(p => p.arrM).sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    return sorted.length % 2
      ? sorted[mid]
      : (sorted[mid - 1] + sorted[mid]) / 2;
  }, [points]);

  const toggleSelection = (accountId: string, additive: boolean) => {
    updateSelectedAccountIds(prev => {
      if (!additive) {
        return new Set([accountId]);
      }
      const next = new Set(prev);
      if (next.has(accountId)) next.delete(accountId);
      else next.add(accountId);
      return next;
    });
  };

  /** Click a selected map bubble to add it to the compare bar (when bar has fewer than 4). */
  const promoteToCompareSlot = useCallback(
    (accountId: string) => {
      if (!selectedIds.has(accountId)) return;
      setCompareSlotIds(prev => {
        if (prev.includes(accountId) || prev.length >= MAX_COMPARE_ACCOUNTS) {
          return prev;
        }
        return [...prev, accountId];
      });
    },
    [selectedIds]
  );

  const removeFromSelection = useCallback(
    (accountId: string) => {
      updateSelectedAccountIds(prev => {
        const next = new Set(prev);
        next.delete(accountId);
        return next;
      });
      setCompareSlotIds(prev => prev.filter(id => id !== accountId));
    },
    [updateSelectedAccountIds]
  );

  const boxModalSelectedIds = useMemo(
    () => new Set(boxModalSelectedAccountIds),
    [boxModalSelectedAccountIds]
  );

  const toggleBoxModalAccount = useCallback((accountId: string) => {
    setBoxModalSelectedAccountIds(prev => {
      const next = new Set(prev);
      if (next.has(accountId)) next.delete(accountId);
      else next.add(accountId);
      return normalizeAccountIds(Array.from(next));
    });
  }, []);

  const handleBoxPickModalOpenChange = useCallback(
    (open: boolean) => {
      if (!open && isSaveWatchlistModalOpen) return;
      if (!open) {
        setBoxPickModalIds(null);
        setBoxModalSelectedAccountIds([]);
        applySelectedAccountIds([]);
        setCompareSlotIds([]);
      }
    },
    [applySelectedAccountIds, isSaveWatchlistModalOpen]
  );

  const handleOpenSaveWatchlistFromBoxModal = useCallback(() => {
    const accountIds = normalizeAccountIds(boxModalSelectedAccountIds);
    if (accountIds.length === 0) return;

    if (onSaveAsWatchlist) {
      onSaveAsWatchlist(accountIds);
      return;
    }

    setWatchlistSaveSeedIds(accountIds);
    setIsSaveWatchlistModalOpen(true);
  }, [boxModalSelectedAccountIds, onSaveAsWatchlist]);

  useEffect(() => {
    if (normalizedSelectedAccountIds.length > 0) {
      setRevenuePopupPeers(prev =>
        prev?.every(id => selectedIds.has(id)) ? prev : null
      );
      setReachHoverAccountId(prev =>
        prev && selectedIds.has(prev) ? prev : null
      );
      return;
    }

    setSelectedCluster(null);
    setPopupAccountId(null);
    setRevenuePopupPeers(null);
    setReachHoverAccountId(null);
  }, [normalizedSelectedAccountIds, selectedIds]);

  /** Reach map geometry (prototype birdsEyeReach). */
  const RW = 470;
  /** Taller plot area so Risk vs Opportunity reads less “squashed” on wide layouts. */
  const RH = 278;
  const padL = 46;
  const padR = 10;
  const padT = 20;
  const padB = 32;
  const plotW = RW - padL - padR;
  const plotH = RH - padT - padB;
  const hx = padL + plotW / 2;
  const hy = padT + plotH / 2;
  /** Pull quadrant labels slightly inward for clearer separation from plot edges / bubbles. */
  const qLabelInset = 2;
  /** Extra pixels between quadrant title and “Exec: …” line (same visual gap in all four corners). */
  const qTitleSubGap = 3;
  /** Baseline distance title → subtitle tuned for slightly smaller map labels. */
  const qTitleToSub = 8 + qTitleSubGap;
  const qTopTitleY = padT + 13 - qLabelInset;
  /** Top corners: nudge subtitle down so title / Exec aren’t cramped. */
  const qTopSubY = qTopTitleY + qTitleToSub;
  /** Bottom corners: anchor subtitle above plot bottom, place title higher so gap matches top. */
  const qBotSubY = padT + plotH - 5 - qLabelInset;
  const qBotTitleY = qBotSubY - qTitleToSub;

  const npxReach = (p: Point) => padL + (p.reachOpp * 0.88 + 0.06) * plotW;
  const npyReach = (p: Point) =>
    padT + (1 - (p.reachRisk * 0.88 + 0.06)) * plotH;

  /** Revenue map: dynamic ARR max on X; dynamic NRR top with 0–80 broken strip on Y. */
  const RevW = 700;
  const RevH = 420;
  const PAD = { l: 60, r: 28, t: 36, b: 60 };
  const revPlotW = RevW - PAD.l - PAD.r;
  const revPlotH = RevH - PAD.t - PAD.b;
  const arrMin = 0;
  const maxArrObserved = useMemo(
    () => Math.max(0, ...points.map(p => p.arrM || 0)),
    [points]
  );
  const arrStep = useMemo(
    () => chooseNiceStep(Math.max(5, maxArrObserved), 8),
    [maxArrObserved]
  );
  const arrMax = useMemo(
    () =>
      Math.max(
        arrStep,
        Math.ceil(Math.max(5, maxArrObserved) / arrStep) * arrStep
      ),
    [arrStep, maxArrObserved]
  );
  const arrTicks = useMemo(() => {
    const ticks: number[] = [];
    for (let v = 0; v <= arrMax + 1e-6; v += arrStep) {
      ticks.push(Number(v.toFixed(6)));
    }
    if (ticks[ticks.length - 1] !== arrMax) ticks.push(arrMax);
    return ticks;
  }, [arrMax, arrStep]);
  const revenueYBottom = PAD.t + revPlotH;
  const revenueYTop = PAD.t;
  const revenueHLow = revPlotH * REVENUE_NRR_BREAK_FRAC;
  const revenueYJoin = revenueYBottom - revenueHLow;
  const maxNrrObserved = useMemo(
    () => Math.max(0, ...points.map(p => p.nrr || 0)),
    [points]
  );
  const nrrTop = useMemo(() => {
    const observed = Math.max(100, maxNrrObserved);
    // Keep visible headroom above the highest bubble so top values do not stick to the edge.
    const withHeadroom = observed + Math.max(5, observed * 0.08);
    return Math.ceil(withHeadroom / 5) * 5;
  }, [maxNrrObserved]);
  const nrrMainSpan = Math.max(5, nrrTop - 80);
  const revenueNrrMainTicks = useMemo(() => {
    // Exactly 11 labels on Y main band: 80, ..., nrrTop.
    const step = nrrMainSpan / 10;
    return Array.from({ length: 11 }, (_, i) =>
      Number((80 + i * step).toFixed(step >= 1 ? 0 : 1))
    );
  }, [nrrTop]);

  const toRevX = (arrM: number) =>
    PAD.l +
    ((clamp(arrM, arrMin, arrMax) - arrMin) / (arrMax - arrMin)) * revPlotW;
  /** 0–80 in bottom strip; 80–top in main band (continuous at 80). */
  const toRevY = (nrr: number) => {
    const n = clamp(nrr, 0, nrrTop);
    if (n <= 80) {
      return revenueYBottom - (n / 80) * revenueHLow;
    }
    return (
      revenueYJoin - ((n - 80) / nrrMainSpan) * (revenueYJoin - revenueYTop)
    );
  };

  const revenueCollisionOffsets = useMemo(
    () =>
      buildCollisionOffsets(
        visiblePoints,
        p => toRevX(p.arrM),
        p => toRevY(p.nrr),
        REVENUE_COLLISION_BASE_PX
      ),
    [
      visiblePoints,
      revPlotW,
      revPlotH,
      PAD.l,
      arrMin,
      arrMax,
      revenueYBottom,
      revenueYJoin,
      revenueYTop,
      revenueHLow,
      nrrTop,
      nrrMainSpan,
    ]
  );

  const reachCollisionOffsets = useMemo(
    () =>
      buildCollisionOffsets(
        visiblePoints,
        npxReach,
        npyReach,
        REACH_COLLISION_BASE_PX
      ),
    [visiblePoints]
  );

  marqueePickRef.current = {
    view,
    points: visiblePoints,
    reachCollisionOffsets,
    revenueCollisionOffsets,
    padL,
    padT,
    plotW,
    plotH,
    padForRev: PAD,
    revPlotW,
    revPlotH,
    npxReach,
    npyReach,
    toRevX,
    toRevY,
  };

  const handlePlotPointerDown = useCallback(
    (e: React.PointerEvent<SVGRectElement>) => {
      if (e.button !== 0) return;
      const svg = plotSvgRef.current;
      if (!svg) return;

      const target = e.target as Element | null;
      if (target?.closest?.("[data-birdseye-node='true']")) return;

      const pt = clientToSvgPoint(svg, e.clientX, e.clientY);
      const api = marqueePickRef.current;
      if (!api) return;

      const inReachPlot =
        api.view === "reach" &&
        pt.x >= api.padL &&
        pt.x <= api.padL + api.plotW &&
        pt.y >= api.padT &&
        pt.y <= api.padT + api.plotH;
      const P = api.padForRev;
      const inRevPlot =
        api.view === "revenue" &&
        pt.x >= P.l &&
        pt.x <= P.l + api.revPlotW &&
        pt.y >= P.t &&
        pt.y <= P.t + api.revPlotH;
      if (!inReachPlot && !inRevPlot) return;

      e.preventDefault();
      e.stopPropagation();

      marqueeSessionRef.current = {
        startClientX: e.clientX,
        startClientY: e.clientY,
        startSvgX: pt.x,
        startSvgY: pt.y,
        pointerId: e.pointerId,
        additive: e.shiftKey,
      };
      setMarqueeBox({ x: pt.x, y: pt.y, width: 0, height: 0 });

      const move = (ev: PointerEvent) => {
        if (ev.pointerId !== marqueeSessionRef.current?.pointerId) return;
        const cur = clientToSvgPoint(svg, ev.clientX, ev.clientY);
        const sess = marqueeSessionRef.current;
        if (!sess) return;
        setMarqueeBox({
          x: Math.min(sess.startSvgX, cur.x),
          y: Math.min(sess.startSvgY, cur.y),
          width: Math.abs(cur.x - sess.startSvgX),
          height: Math.abs(cur.y - sess.startSvgY),
        });
      };

      const up = (ev: PointerEvent) => {
        if (ev.pointerId !== marqueeSessionRef.current?.pointerId) return;
        window.removeEventListener("pointermove", move);
        window.removeEventListener("pointerup", up);
        window.removeEventListener("pointercancel", up);

        const sess = marqueeSessionRef.current;
        marqueeSessionRef.current = null;
        setMarqueeBox(null);
        if (!sess) return;

        const dx = ev.clientX - sess.startClientX;
        const dy = ev.clientY - sess.startClientY;
        if (Math.hypot(dx, dy) < MARQUEE_MIN_DRAG_CLIENT_PX) return;

        const cur = clientToSvgPoint(svg, ev.clientX, ev.clientY);
        const rx = Math.min(sess.startSvgX, cur.x);
        const ry = Math.min(sess.startSvgY, cur.y);
        const rw = Math.abs(cur.x - sess.startSvgX);
        const rh = Math.abs(cur.y - sess.startSvgY);

        const api2 = marqueePickRef.current;
        if (!api2) return;
        const picked: string[] = [];
        const rNode = api2.view === "reach" ? REACH_NODE_R : REVENUE_NODE_R;

        if (api2.view === "reach") {
          api2.points.forEach(p => {
            const offset = api2.reachCollisionOffsets.get(p.account_id) || {
              dx: 0,
              dy: 0,
            };
            const cx = api2.npxReach(p) + offset.dx;
            const cy = api2.npyReach(p) + offset.dy;
            if (circleIntersectsRect(cx, cy, rNode, rx, ry, rw, rh)) {
              picked.push(p.account_id);
            }
          });
        } else {
          api2.points.forEach(p => {
            const revOff = api2.revenueCollisionOffsets.get(p.account_id) ?? {
              dx: 0,
              dy: 0,
            };
            const cx = api2.toRevX(p.arrM) + revOff.dx;
            const cy = api2.toRevY(p.nrr) + revOff.dy;
            if (circleIntersectsRect(cx, cy, rNode, rx, ry, rw, rh)) {
              picked.push(p.account_id);
            }
          });
        }

        if (picked.length >= 2 && !sess.additive) {
          const uniquePicked = normalizeAccountIds(picked);
          applySelectedAccountIds(uniquePicked);
          setBoxPickModalIds(uniquePicked);
          setBoxModalSelectedAccountIds(uniquePicked);
          setPopupAccountId(null);
          setReachHoverAccountId(null);
          setRevenuePopupPeers(null);
        } else {
          updateSelectedAccountIds(prev => {
            if (sess.additive) {
              const next = new Set(prev);
              picked.forEach(id => next.add(id));
              return next;
            }
            if (picked.length === 0) return prev;
            return new Set(picked);
          });
        }
      };

      window.addEventListener("pointermove", move);
      window.addEventListener("pointerup", up);
      window.addEventListener("pointercancel", up);
    },
    [applySelectedAccountIds, updateSelectedAccountIds]
  );

  const execBadgeTier = (eng: number) =>
    getExecAttentionTier(eng, execAttnCaution, execAttnCritical);

  const renderExecAttentionBadge = (
    cx: number,
    cy: number,
    tier: "critical" | "caution" | "none",
    r = 6
  ) => {
    return null;
  };

  if (loading)
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto"></div>
        <p className="text-gray-500 mt-2 text-sm">
          Loading Bird&apos;s Eye view...
        </p>
      </div>
    );
  if (error) return <div className="p-6 text-sm text-red-500">{error}</div>;

  const selectionStrip =
    selectedAccounts.length > 0 && selectionBarDock ? (
      <div
        className="pointer-events-none fixed z-[1]"
        style={{
          left: selectionBarDock.left,
          width: selectionBarDock.width,
          bottom: selectionBarDock.bottom,
        }}
      >
        <div className={FLOATING_SELECTION_BAR_PANEL}>
          <ShoppingBag
            className="h-5 w-5 shrink-0 text-slate-400"
            strokeWidth={1.75}
            aria-hidden
          />

          <div className="flex min-w-0 flex-1 items-center gap-2 overflow-x-auto py-0.5 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {selectionStripVisibleAccounts.map(a => {
              const ringColor = hColor(a.overall, bhThresholds);
              const fill = NODE_FILL_MAP[a.cluster] || NODE_FILL_FALLBACK;
              return (
                <button
                  key={a.account_id}
                  type="button"
                  onClick={() => removeFromSelection(a.account_id)}
                  className="group relative shrink-0"
                  title={`Remove ${a.account_name} from selection`}
                  aria-label={`Remove ${a.account_name} from selection`}
                >
                  <span
                    className="flex h-9 w-9 items-center justify-center rounded-full border-2 text-[10px] font-bold leading-none text-slate-700 sm:h-10 sm:w-10 sm:text-[11px]"
                    style={{
                      borderColor: ringColor,
                      backgroundColor: fill,
                    }}
                  >
                    {initials(a.account_name)}
                  </span>
                  <span className="absolute -right-0.5 -top-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-rose-500 text-white shadow ring-2 ring-white transition-opacity group-hover:bg-rose-600">
                    <X className="h-2.5 w-2.5" strokeWidth={3} />
                  </span>
                </button>
              );
            })}
            {selectionStripOverflowCount > 0 ? (
              <span
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border-2 border-dashed border-slate-300 bg-slate-50 text-[10px] font-bold text-slate-600 sm:h-10 sm:w-10 sm:text-[11px]"
                title={`${selectionStripOverflowCount} more selected account${selectionStripOverflowCount !== 1 ? "s" : ""}`}
              >
                +{selectionStripOverflowCount}
              </span>
            ) : null}
          </div>
          <div className="ml-auto flex shrink-0 flex-wrap items-center justify-end gap-1.5">
            <button
              type="button"
              onClick={handleOpenSaveWatchlist}
              className={SELECTION_BAR_OUTLINE_BTN}
              title="Save selected accounts as a watchlist"
            >
              <Bookmark className="h-3 w-3 shrink-0" strokeWidth={2} />
              Save as watchlist
            </button>
            {view === "reach" ? (
              <button
                type="button"
                onClick={() => {
                  setView("revenue");
                  closeNodePopup();
                }}
                className={SELECTION_BAR_OUTLINE_BTN}
                title="Open Revenue Map with this selection"
              >
                <ArrowRight className="h-3 w-3 shrink-0" strokeWidth={2.5} />
                Revenue Map
              </button>
            ) : (
              <button
                type="button"
                onClick={() => {
                  setView("reach");
                  closeNodePopup();
                }}
                className={SELECTION_BAR_OUTLINE_BTN}
                title="Open Risk-Opportunity Map with this selection"
              >
                <Radar className="h-3 w-3 shrink-0" strokeWidth={2} />
                Risk-Opportunity Map
              </button>
            )}
            <button
              type="button"
              disabled={!canOpenAccountCompare}
              title={compareButtonTitle}
              onClick={handleOpenAccountCompare}
              className={SELECTION_BAR_PRIMARY_BTN}
            >
              Compare ({compareSlotIds.length}) · {MAX_COMPARE_ACCOUNTS} max
            </button>
            <button
              type="button"
              onClick={() => {
                applySelectedAccountIds([]);
                setCompareSlotIds([]);
              }}
              className="px-1 py-0.5 text-[11px] font-semibold text-violet-700 hover:text-violet-900 hover:underline"
            >
              Clear
            </button>
          </div>
        </div>
      </div>
    ) : null;

  const headerReachRevenue = (
    <div className="border-b flex flex-wrap items-center justify-between gap-2">
      <div className="flex min-w-0 flex-wrap items-center gap-2">
        <TabsComponent
          tabs={VIEW_PAIR.map(({ id, label, icon }) => ({
            id,
            label,
            icon,
          }))}
          activeTab={view}
          onTabChange={(id: string) => {
            setView(id as BirdsEyeView);
            closeNodePopup();
          }}
          variant="tab-underline"
          className="mb-0 whitespace-nowrap"
        />
      </div>
      {!showMapEmpty ? (
        <div className="flex flex-wrap items-center justify-end gap-2">
          {view === "reach" && (
            <>
              <span className="inline-flex flex-wrap items-center gap-1"></span>
              <span className="text-[9px] text-slate-400">
                · Hover for insight · Click bubble for account actions ·
                Shift-click to select · Drag on the plot to box-select ·
                Shift-drag to add
              </span>
            </>
          )}
          {view === "revenue" && (
            <span className="max-w-xl text-right text-[9.5px] text-slate-400">
              ARR × NRR · markers match Risk–Opportunity styling · health dot =
              score band · click for details · shift-click to select · drag on
              the plot to box-select · shift-drag to add
            </span>
          )}
        </div>
      ) : null}
    </div>
  );
  const quadrantStrip =
    view === "reach" ? (
      <div className="mt-3 border-t border-slate-200 pt-2">
        <div className="mb-2 flex items-center justify-between">
          <span className="text-[12px] font-semibold text-black">
            Quadrant Actions
          </span>
          <button
            type="button"
            className="text-[12px] font-medium text-slate-500 hover:text-slate-800"
            onClick={() => setQuadrantStripOpen(o => !o)}
          >
            {quadrantStripOpen ? "▲ Collapse" : "▼ Expand"}
          </button>
        </div>
        {quadrantStripOpen && (
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4">
            {(["triage", "defend", "delegate", "expand"] as const).map(q => {
              const meta = QUADRANT_STYLES[q];
              const qa = QUADRANT_ACTIONS[q];
              const n = quadrantCounts[q];
              return (
                <div
                  key={q}
                  className="rounded-lg border border-slate-100 bg-slate-50/80 p-3.5 pl-4"
                  style={{ borderLeftWidth: 3, borderLeftColor: qa.border }}
                >
                  <div
                    className="text-sm font-bold"
                    style={{ color: qa.titleColor }}
                  >
                    {meta.stripTitle}
                  </div>
                  <div className="mt-0.5 text-[12px] text-slate-500">
                    {n} account{n !== 1 ? "s" : ""} · {meta.stripBlurb}
                  </div>
                  <ul className="mt-2 list-disc space-y-1 pl-4 text-[12px] text-slate-600">
                    {qa.items.map(item => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                  <button
                    type="button"
                    className="mt-2 text-[12px] font-semibold text-violet-700 hover:underline"
                    onClick={() => {
                      setView("revenue");
                      closeNodePopup();
                    }}
                  >
                    Revenue Lens →
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    ) : null;

  const renderReachNodes = () => {
    const r = REACH_NODE_R;
    const badgeR = 3.8;
    const badgeOffset = r * 0.88;
    const selActive = selectedIds.size > 0;

    // SVG paint order: later siblings on top — draw selected accounts last
    return [...visiblePoints]
      .sort((a, b) => {
        const aSel = selectedIds.has(a.account_id) ? 1 : 0;
        const bSel = selectedIds.has(b.account_id) ? 1 : 0;
        return aSel - bSel || a.account_id.localeCompare(b.account_id);
      })
      .map(p => {
        const offset = reachCollisionOffsets.get(p.account_id) || {
          dx: 0,
          dy: 0,
        };
        const apx = npxReach(p) + offset.dx;
        const apy = npyReach(p) + offset.dy;
        const sc = hColor(p.overall, bhThresholds);
        const nodeFill = NODE_FILL_MAP[p.cluster] || NODE_FILL_FALLBACK;
        const inSel = selectedIds.has(p.account_id);
        const bx = apx + badgeOffset;
        const by = apy - badgeOffset;
        const tier = execBadgeTier(p.execEngagement);
        const exBx = apx + badgeOffset;
        const exBy = apy + badgeOffset;

        return (
          <g
            key={p.account_id}
            data-birdseye-node="true"
            opacity={1}
            style={{ cursor: "pointer" }}
            onMouseEnter={e => {
              clearReachHoverLeaveTimer();
              setReachHoverAccountId(p.account_id);
              setReachHoverPos({ x: e.clientX, y: e.clientY });
            }}
            onMouseMove={e => {
              setReachHoverPos({ x: e.clientX, y: e.clientY });
            }}
            onMouseLeave={() => scheduleReachHoverHide()}
            onClick={e => {
              const additive = (e as React.MouseEvent).shiftKey;
              clearReachHoverLeaveTimer();
              setReachHoverAccountId(null);
              if (additive) {
                toggleSelection(p.account_id, true);
                return;
              }
              if (
                selectedIds.has(p.account_id) &&
                !compareSlotIds.includes(p.account_id) &&
                compareSlotIds.length < MAX_COMPARE_ACCOUNTS
              ) {
                promoteToCompareSlot(p.account_id);
                return;
              }
              positionPopupNearNode(e.currentTarget as SVGGElement, "reach");
              setPopupAccountId(p.account_id);
            }}
          >
            {selActive && inSel && (
              <circle
                cx={apx}
                cy={apy}
                r={r + SELECTION_RING_GAP}
                fill="none"
                stroke="#7c3aed"
                strokeWidth={1.5}
                strokeDasharray="3 2"
                opacity={0.85}
              />
            )}
            <circle
              cx={apx}
              cy={apy}
              r={r}
              fill={nodeFill}
              stroke="#d1d5db"
              strokeWidth={1.1}
            />
            <text
              x={apx}
              y={apy - 1.6}
              textAnchor="middle"
              dominantBaseline="central"
              fontSize={6.4}
              fontWeight={700}
              fill="#374151"
            >
              {initials(p.account_name)}
            </text>
            <text
              x={apx}
              y={apy + 2.9}
              textAnchor="middle"
              dominantBaseline="central"
              fontSize={5.9}
              fontWeight={600}
              fill="#6b7280"
            >
              {Math.round(p.overall)}
            </text>
            <circle
              cx={bx}
              cy={by}
              r={badgeR}
              fill={sc}
              stroke="#fff"
              strokeWidth={1.2}
            />
            {renderExecAttentionBadge(exBx, exBy, tier, badgeR)}
          </g>
        );
      });
  };

  const renderRevenueNodes = () =>
    [...visiblePoints]
      .sort((a, b) => {
        // Selected accounts paint last (on top of overlaps)
        const aSel = selectedIds.has(a.account_id) ? 1 : 0;
        const bSel = selectedIds.has(b.account_id) ? 1 : 0;
        return (
          aSel - bSel ||
          revenueStackKey(a).localeCompare(revenueStackKey(b)) ||
          a.account_id.localeCompare(b.account_id)
        );
      })
      .map(p => {
        const r = REVENUE_NODE_R;
        const badgeR = 5.5;
        const badgeOffset = r * 0.72;
        const sc = hColor(p.overall, bhThresholds);
        const nodeFill = NODE_FILL_MAP[p.cluster] || NODE_FILL_FALLBACK;
        const inSel = selectedIds.has(p.account_id);
        const selActive = selectedIds.size > 0;
        const revOff = revenueCollisionOffsets.get(p.account_id) ?? {
          dx: 0,
          dy: 0,
        };
        const apx = toRevX(p.arrM) + revOff.dx;
        const apy = toRevY(p.nrr) + revOff.dy;
        const bx = apx + badgeOffset;
        const by = apy - badgeOffset;
        const tier = execBadgeTier(p.execEngagement);
        const exBx = apx + badgeOffset;
        const exBy = apy + badgeOffset;

        return (
          <g
            key={p.account_id}
            data-birdseye-node="true"
            opacity={1}
            style={{ cursor: "pointer" }}
            onMouseEnter={e => {
              clearRevenueHoverLeaveTimer();
              setRevenueHoverAccountId(p.account_id);
              setRevenueHoverPos({ x: e.clientX, y: e.clientY });
            }}
            onMouseMove={e => {
              setRevenueHoverPos({ x: e.clientX, y: e.clientY });
            }}
            onMouseLeave={() => scheduleRevenueHoverHide()}
            onClick={e => {
              const additive = (e as React.MouseEvent).shiftKey;
              clearRevenueHoverLeaveTimer();
              setRevenueHoverAccountId(null);
              const peers = revenueBuckets.get(revenueStackKey(p)) ?? [p];
              if (additive) {
                updateSelectedAccountIds(prev => {
                  const next = new Set(prev);
                  peers.forEach(pt => next.add(pt.account_id));
                  return next;
                });
                return;
              }
              if (
                selectedIds.has(p.account_id) &&
                !compareSlotIds.includes(p.account_id) &&
                compareSlotIds.length < MAX_COMPARE_ACCOUNTS
              ) {
                promoteToCompareSlot(p.account_id);
                return;
              }
              const ids = peers
                .map(pt => pt.account_id)
                .sort((a, b) => a.localeCompare(b));
              positionPopupNearNode(
                e.currentTarget as SVGGElement,
                "revenue",
                ids.length
              );
              setRevenuePopupPeers(ids.length > 1 ? ids : null);
              setPopupAccountId(p.account_id);
            }}
          >
            {selActive && inSel && (
              <circle
                cx={apx}
                cy={apy}
                r={r + SELECTION_RING_GAP}
                fill="none"
                stroke="#7c3aed"
                strokeWidth={1.5}
                strokeDasharray="3 2"
                opacity={0.85}
              />
            )}
            <circle
              cx={apx}
              cy={apy}
              r={r}
              fill={nodeFill}
              stroke="#d1d5db"
              strokeWidth={1.5}
            />
            <text
              x={apx}
              y={apy}
              textAnchor="middle"
              dominantBaseline="central"
              fontSize={8.5}
              fontWeight={700}
              fill="#374151"
            >
              {initials(p.account_name)}
            </text>
            <circle
              cx={bx}
              cy={by}
              r={badgeR}
              fill={sc}
              stroke="#fff"
              strokeWidth={1.2}
            />
            {renderExecAttentionBadge(exBx, exBy, tier, badgeR)}
          </g>
        );
      });

  const mapEmptyPanel =
    showMapEmpty && mapEmptyReason ? (
      <BirdsEyeMapEmptyState
        variant={view}
        reason={mapEmptyReason}
        standalone
        clusterLabel={
          selectedCluster ? CLUSTER_META[selectedCluster].label : undefined
        }
        totalAccountCount={totalSelectedAccountCount}
        accountsWithoutDigestCount={Math.max(
          0,
          totalSelectedAccountCount - digestAccounts.length
        )}
        onClearClusterFilter={
          mapEmptyReason === "cluster-filter"
            ? () => setSelectedCluster(null)
            : undefined
        }
      />
    ) : null;

  return (
    <div
      ref={panelBoundsRef}
      className="relative flex h-full min-h-[calc(100vh-15.5rem)] min-h-0 min-w-0 flex-col overflow-hidden bg-white"
    >
      <div className="shrink-0 px-4 pt-2">
        <SyntheticDataModeBanner />
      </div>
      <div className="shrink-0 px-4">{headerReachRevenue}</div>

      <div
        className="flex min-h-0 flex-1 flex-col px-4"
        style={{
          paddingBottom:
            mapEmptyPanel || selectedIds.size === 0 ? "1rem" : "6.5rem",
        }}
      >
        {mapEmptyPanel ? (
          mapEmptyPanel
        ) : view === "reach" ? (
          <>
            <div className="flex min-h-[min(360px,calc(100vh-18rem))] flex-1 flex-col">
              <div className="flex min-h-0 flex-1 items-stretch gap-3.5">
                <div
                  ref={plotContainerRef}
                  className="relative flex min-h-0 min-w-0 flex-1 flex-col justify-center"
                >
                  <svg
                    ref={plotSvgRef}
                    className="block h-full w-full min-h-[240px] select-none"
                    viewBox={`0 0 ${RW} ${RH}`}
                    preserveAspectRatio="xMidYMid meet"
                  >
                    <rect
                      x={padL}
                      y={padT}
                      width={plotW}
                      height={plotH}
                      fill="#fafbfc"
                      rx={5}
                    />
                    <rect
                      x={padL}
                      y={padT}
                      width={plotW}
                      height={plotH}
                      rx={5}
                      fill="rgba(226,232,240,0.32)"
                    />
                    <line
                      x1={padL}
                      y1={hy}
                      x2={padL + plotW}
                      y2={hy}
                      stroke="#b5b5b5"
                      strokeWidth={0.5}
                      strokeDasharray="4 4"
                    />
                    <line
                      x1={hx}
                      y1={padT}
                      x2={hx}
                      y2={padT + plotH}
                      stroke="#b5b5b5"
                      strokeWidth={0.5}
                      strokeDasharray="4 4"
                    />

                    <line
                      x1={padL}
                      y1={padT + plotH}
                      x2={padL}
                      y2={padT + 2}
                      stroke="#d1d5db"
                      strokeWidth={1}
                    />
                    <polygon
                      points={`${padL},${padT - 2} ${padL - 3},${padT + 7} ${padL + 3},${padT + 7}`}
                      fill="#9ca3af"
                    />
                    <text
                      x={13}
                      y={padT + plotH / 2}
                      textAnchor="middle"
                      fontSize={8.5}
                      fill="#9ca3af"
                      fontWeight={500}
                      transform={`rotate(-90,13,${padT + plotH / 2})`}
                    >
                      Risk
                    </text>
                    <text
                      x={padL - 6}
                      y={padT + plotH / 4}
                      dominantBaseline="central"
                      textAnchor="end"
                      fontSize={7}
                      fill="#b8c0cc"
                    >
                      High
                    </text>
                    <text
                      x={padL - 6}
                      y={padT + (3 * plotH) / 4}
                      dominantBaseline="central"
                      textAnchor="end"
                      fontSize={7}
                      fill="#b8c0cc"
                    >
                      Low
                    </text>

                    <line
                      x1={padL}
                      y1={padT + plotH}
                      x2={padL + plotW - 2}
                      y2={padT + plotH}
                      stroke="#d1d5db"
                      strokeWidth={1}
                    />
                    <polygon
                      points={`${padL + plotW + 2},${padT + plotH} ${padL + plotW - 7},${padT + plotH - 3} ${padL + plotW - 7},${padT + plotH + 3}`}
                      fill="#9ca3af"
                    />
                    <text
                      x={padL + plotW / 2}
                      y={RH - 4}
                      textAnchor="middle"
                      fontSize={8.5}
                      fill="#9ca3af"
                      fontWeight={500}
                    >
                      Growth Opportunity
                    </text>
                    <text
                      x={padL + plotW / 4}
                      y={padT + plotH + 14}
                      textAnchor="middle"
                      fontSize={7}
                      fill="#b8c0cc"
                    >
                      Low
                    </text>
                    <text
                      x={padL + (3 * plotW) / 4}
                      y={padT + plotH + 14}
                      textAnchor="middle"
                      fontSize={7}
                      fill="#b8c0cc"
                    >
                      High
                    </text>

                    <text
                      x={padL + 8}
                      y={qTopTitleY}
                      fontSize={8}
                      fontWeight={600}
                      fill="#f87171"
                    >
                      Triage
                    </text>
                    <text x={padL + 8} y={qTopSubY} fontSize={7} fill="#b0bac7">
                      Exec: Intervene
                    </text>
                    <text
                      x={padL + plotW - 8}
                      y={qTopTitleY}
                      textAnchor="end"
                      fontSize={8}
                      fontWeight={600}
                      fill="#fb923c"
                    >
                      Defend
                    </text>
                    <text
                      x={padL + plotW - 8}
                      y={qTopSubY}
                      textAnchor="end"
                      fontSize={7}
                      fill="#b0bac7"
                    >
                      Exec: Protect
                    </text>
                    <text
                      x={padL + 8}
                      y={qBotTitleY}
                      fontSize={8}
                      fontWeight={500}
                      fill="#a8b4c0"
                    >
                      Monitor
                    </text>
                    <text x={padL + 8} y={qBotSubY} fontSize={7} fill="#b0bac7">
                      Exec: Monitor
                    </text>
                    <text
                      x={padL + plotW - 8}
                      y={qBotTitleY}
                      textAnchor="end"
                      fontSize={8}
                      fontWeight={600}
                      fill="#22c55e"
                    >
                      Expand
                    </text>
                    <text
                      x={padL + plotW - 8}
                      y={qBotSubY}
                      textAnchor="end"
                      fontSize={7}
                      fill="#b0bac7"
                    >
                      Exec: Enable
                    </text>

                    <rect
                      x={padL}
                      y={padT}
                      width={plotW}
                      height={plotH}
                      fill="transparent"
                      style={{ cursor: "crosshair", touchAction: "none" }}
                      onPointerDown={handlePlotPointerDown}
                    />

                    {selectedCluster &&
                      (() => {
                        const pts = visiblePoints.filter(
                          p => p.cluster === selectedCluster
                        );
                        if (!pts.length) return null;
                        const xs = pts.map(npxReach);
                        const ys = pts.map(npyReach);
                        const minX = Math.min(...xs);
                        const maxX = Math.max(...xs);
                        const minY = Math.min(...ys);
                        const maxY = Math.max(...ys);
                        const padBox = 12 + 20;
                        const rx = Math.max(padL + 1, minX - padBox);
                        const ry = Math.max(padT + 1, minY - padBox);
                        const rRight = Math.min(
                          padL + plotW - 1,
                          maxX + padBox
                        );
                        const rBottom = Math.min(
                          padT + plotH - 1,
                          maxY + padBox
                        );
                        const m = CLUSTER_META[selectedCluster];
                        return (
                          <rect
                            pointerEvents="none"
                            x={rx}
                            y={ry}
                            width={Math.max(rRight - rx, 30)}
                            height={Math.max(rBottom - ry, 30)}
                            rx={12}
                            fill={m.color}
                            fillOpacity={0.09}
                            stroke={m.color}
                            strokeOpacity={0.4}
                            strokeWidth={1.5}
                          />
                        );
                      })()}

                    {renderReachNodes()}
                    {marqueeBox ? (
                      <rect
                        pointerEvents="none"
                        x={marqueeBox.x}
                        y={marqueeBox.y}
                        width={marqueeBox.width}
                        height={marqueeBox.height}
                        fill="rgba(124,58,237,0.12)"
                        stroke="#7c3aed"
                        strokeWidth={1}
                        strokeDasharray="5 4"
                        rx={2}
                      />
                    ) : null}
                  </svg>

                  {popupPoint && view === "reach" && (
                    <div
                      className="absolute z-10 w-[min(100%,288px)] max-w-[calc(100%-0.5rem)]"
                      style={{
                        left: popupCardPos?.left ?? MAP_POPUP_EDGE_PX,
                        top: popupCardPos?.top ?? MAP_POPUP_EDGE_PX,
                      }}
                    >
                      <ReachMapHoverCard
                        point={popupPoint}
                        accountData={popupAccountData}
                        selectedIds={selectedIds}
                        toggleSelection={toggleSelection}
                        onClose={closeNodePopup}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="max-h-[38vh] shrink-0 overflow-y-auto">
              {quadrantStrip}
            </div>
          </>
        ) : (
          <div className="flex min-h-0 flex-1 flex-col">
            <div ref={plotContainerRef} className="relative w-full shrink-0">
              <svg
                ref={plotSvgRef}
                className="block w-full select-none"
                viewBox={`0 0 ${RevW} ${RevH}`}
                preserveAspectRatio="xMidYMid meet"
              >
                <rect
                  x={PAD.l}
                  y={PAD.t}
                  width={revPlotW}
                  height={revPlotH}
                  fill="#f9fafb"
                  rx={5}
                  stroke="#e2e8f0"
                  strokeWidth={1.2}
                />
                {revenueNrrMainTicks.map((nrr, idx) => {
                  const y = toRevY(nrr);
                  const is100 = nrr === 100;
                  const isMajorDecade = idx % 2 === 0;
                  const stroke = is100
                    ? "#9aa8ba"
                    : isMajorDecade
                      ? "#edf1f6"
                      : "#f1f5f9";
                  const strokeW = is100 ? 1.5 : isMajorDecade ? 0.8 : 0.55;
                  return (
                    <g key={nrr}>
                      <line
                        x1={PAD.l}
                        y1={y}
                        x2={PAD.l + revPlotW}
                        y2={y}
                        stroke={stroke}
                        strokeWidth={strokeW}
                        strokeDasharray={is100 ? "7 4" : "none"}
                      />
                      <text
                        x={PAD.l - 5}
                        y={y}
                        textAnchor="end"
                        dominantBaseline="middle"
                        fontSize={9.5}
                        fill={is100 ? "#7b8a9c" : "#c0c8d4"}
                      >
                        {nrr}%
                      </text>
                    </g>
                  );
                })}
                <line
                  x1={PAD.l}
                  y1={revenueYBottom}
                  x2={PAD.l + revPlotW}
                  y2={revenueYBottom}
                  stroke="#e2e8f0"
                  strokeWidth={0.9}
                />
                <path
                  d={`M ${PAD.l - 1} ${revenueYBottom - 4} l 5,-4 M ${PAD.l - 1} ${revenueYBottom - 12} l 5,-4`}
                  stroke="#c0c8d4"
                  strokeWidth={0.9}
                  fill="none"
                />
                {arrTicks.map(arr => {
                  const x = toRevX(arr);
                  return (
                    <g key={arr}>
                      <line
                        x1={x}
                        y1={PAD.t}
                        x2={x}
                        y2={PAD.t + revPlotH}
                        stroke="#edf1f6"
                        strokeWidth={0.8}
                      />
                      <text
                        x={x}
                        y={PAD.t + revPlotH + 16}
                        textAnchor="middle"
                        fontSize={10}
                        fill="#c0c8d4"
                      >
                        ${arr}M
                      </text>
                    </g>
                  );
                })}
                <line
                  x1={PAD.l}
                  y1={toRevY(100)}
                  x2={PAD.l + revPlotW}
                  y2={toRevY(100)}
                  stroke="#c0c8d4"
                  strokeWidth={1}
                  strokeDasharray="4 4"
                />
                <text
                  x={PAD.l + 6}
                  y={toRevY(100) - 5}
                  fontSize={9.5}
                  fill="#94a3b8"
                >
                  NRR = 100% (expansion boundary)
                </text>
                <line
                  x1={toRevX(arrMedianM)}
                  y1={PAD.t}
                  x2={toRevX(arrMedianM)}
                  y2={PAD.t + revPlotH}
                  stroke="#c0c8d4"
                  strokeWidth={1}
                  strokeDasharray="4 4"
                />
                <text
                  x={toRevX(arrMedianM)}
                  y={PAD.t - 6}
                  textAnchor="middle"
                  fontSize={9}
                  fill="#94a3b8"
                >
                  Portfolio median (${arrMedianM.toFixed(1)}M)
                </text>
                <text
                  x={PAD.l + revPlotW / 2}
                  y={RevH - 8}
                  textAnchor="middle"
                  fontSize={12}
                  fontWeight={500}
                  fill="#64748b"
                >
                  ARR ($M)
                </text>
                <text
                  x={14}
                  y={PAD.t + revPlotH / 2}
                  textAnchor="middle"
                  fontSize={12}
                  fontWeight={500}
                  fill="#64748b"
                  transform={`rotate(-90,14,${PAD.t + revPlotH / 2})`}
                >
                  NRR (%)
                </text>
                <rect
                  x={PAD.l}
                  y={PAD.t}
                  width={revPlotW}
                  height={revPlotH}
                  fill="transparent"
                  style={{ cursor: "crosshair", touchAction: "none" }}
                  onPointerDown={handlePlotPointerDown}
                />
                {renderRevenueNodes()}
                {marqueeBox ? (
                  <rect
                    pointerEvents="none"
                    x={marqueeBox.x}
                    y={marqueeBox.y}
                    width={marqueeBox.width}
                    height={marqueeBox.height}
                    fill="rgba(124,58,237,0.12)"
                    stroke="#7c3aed"
                    strokeWidth={1}
                    strokeDasharray="5 4"
                    rx={2}
                  />
                ) : null}
              </svg>

              {popupPoint && view === "revenue" && (
                <div
                  className="absolute z-10 w-[min(100%,268px)] max-w-[calc(100%-0.5rem)] rounded-lg border border-slate-200 bg-white p-2 shadow-md"
                  style={{
                    left: popupCardPos?.left ?? MAP_POPUP_EDGE_PX,
                    top: popupCardPos?.top ?? MAP_POPUP_EDGE_PX,
                  }}
                >
                  {revenuePopupPeers && revenuePopupPeers.length > 1 ? (
                    <div className="mb-2 rounded-md border border-slate-200 bg-slate-50 px-1.5 py-1.5">
                      <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                        Same ARR &amp; NRR · {revenuePopupPeers.length} accounts
                      </p>
                      <div className="flex max-h-[88px] flex-col gap-0.5 overflow-y-auto">
                        {revenuePopupPeers.map(id => {
                          const pt = points.find(q => q.account_id === id);
                          if (!pt) return null;
                          const active = id === popupAccountId;
                          return (
                            <button
                              key={id}
                              type="button"
                              onClick={() => setPopupAccountId(id)}
                              className={`truncate rounded border px-1.5 py-0.5 text-left text-[11px] font-medium transition-colors ${
                                active
                                  ? "border-violet-300 bg-violet-50 text-violet-800"
                                  : "border-slate-200 bg-white text-slate-700 hover:bg-slate-100"
                              }`}
                            >
                              {pt.account_name}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ) : null}
                  <RevenueMapClickPanel
                    point={popupPoint}
                    accountData={popupAccountData}
                    selectedIds={selectedIds}
                    toggleSelection={toggleSelection}
                    onClose={closeNodePopup}
                  />
                </div>
              )}
            </div>
            <div className="min-h-0 flex-1 bg-white" aria-hidden />
          </div>
        )}
      </div>

      {selectionStrip}

      <Dialog
        open={isCompareModalOpen}
        onOpenChange={handleCompareModalOpenChange}
      >
        <DialogContent
          hideClose
          className="flex max-h-[92vh] w-[95vw] max-w-[980px] flex-col overflow-hidden rounded-[14px] border border-slate-200 bg-white p-0 shadow-2xl"
        >
          <SpiderCompareModalContent
            allAccounts={compareSpiderAccounts}
            selectedAccountIds={compareModalSelectedIds}
            categoryOrder={compareCategoryOrder}
            compareColors={COMPARE_COLORS}
            onToggleAccount={toggleCompareModalSelection}
            onClose={() => handleCompareModalOpenChange(false)}
            validationMessage={compareSelectionMessage}
          />
        </DialogContent>
      </Dialog>

      <BirdsEyeBoxSelectionModal
        open={boxPickModalIds !== null && boxPickModalIds.length >= 2}
        onOpenChange={handleBoxPickModalOpenChange}
        view={view}
        accountIds={boxPickModalIds ?? []}
        pointsById={pointsById}
        selectedIds={boxModalSelectedIds}
        onToggleAccount={toggleBoxModalAccount}
        execAttnCaution={execAttnCaution}
        execAttnCritical={execAttnCritical}
        reachLayout={{
          rw: RW,
          rh: RH,
          padL,
          padT,
          plotW,
          plotH,
          hx,
          hy,
          qTopTitleY,
          qTopSubY,
          qBotTitleY,
          qBotSubY,
        }}
        revenueLayout={{
          revW: RevW,
          revH: RevH,
          padL: PAD.l,
          padR: PAD.r,
          padT: PAD.t,
          padB: PAD.b,
          revPlotW,
          revPlotH,
          arrMin,
          arrMax,
          arrTicks,
          arrMedianM,
          revenueNrrMainTicks,
          revenueYBottom,
          revenueYJoin,
          revenueYTop,
          revenueHLow,
          nrrTop,
          nrrMainSpan,
        }}
        onSaveWatchlist={handleOpenSaveWatchlistFromBoxModal}
        onSwitchMapView={() => {
          setView(prev => (prev === "reach" ? "revenue" : "reach"));
          closeNodePopup();
        }}
        compareAccounts={compareSpiderAccounts}
        compareCategoryOrder={compareCategoryOrder}
        compareColors={COMPARE_COLORS}
      />

      {!onSaveAsWatchlist ? (
        <CreateWatchlistModal
          isOpen={isSaveWatchlistModalOpen}
          onClose={() => {
            setIsSaveWatchlistModalOpen(false);
            setWatchlistSaveSeedIds(null);
          }}
          pickerAccounts={watchlistPickerAccounts}
          initialSelectedIds={watchlistInitialSelectedIds}
          title="Save Accounts"
          nameLabel="Watchlist Name"
          namePlaceholder="Enter Watchlist Name"
          submitLabel="Save Watchlist"
          onSuccess={onSaveAccountsSuccess}
        />
      ) : null}

      {view === "reach" &&
        reachHoverPoint &&
        reachHoverAccountId &&
        reachHoverFixedPosition &&
        !popupAccountId && (
          <div
            style={{
              position: "fixed",
              left: reachHoverFixedPosition.left,
              top: reachHoverFixedPosition.top,
              zIndex: 60,
            }}
            onMouseEnter={clearReachHoverLeaveTimer}
            onMouseLeave={scheduleReachHoverHide}
          >
            <ReachMapAccountHoverCard
              point={reachHoverPoint}
              execAttnCaution={execAttnCaution}
              execAttnCritical={execAttnCritical}
            />
          </div>
        )}

      {view === "revenue" &&
        revenueHoverPoint &&
        revenueHoverAccountId &&
        revenueHoverFixedPosition &&
        !popupAccountId && (
          <div
            style={{
              position: "fixed",
              left: revenueHoverFixedPosition.left,
              top: revenueHoverFixedPosition.top,
              zIndex: 60,
            }}
            onMouseEnter={clearRevenueHoverLeaveTimer}
            onMouseLeave={scheduleRevenueHoverHide}
          >
            <RevenueMapAccountHoverCard point={revenueHoverPoint} />
          </div>
        )}
    </div>
  );
};

function ReachScoreTrendIcon({ overall }: { overall: number }) {
  if (overall >= 75) {
    return (
      <TrendingUp
        className="h-3.5 w-3.5 shrink-0 text-emerald-600"
        strokeWidth={2.5}
        aria-hidden
      />
    );
  }
  if (overall >= 50) {
    return (
      <Minus
        className="h-3.5 w-3.5 shrink-0 text-amber-600"
        strokeWidth={2.5}
        aria-hidden
      />
    );
  }
  return (
    <TrendingDown
      className="h-3.5 w-3.5 shrink-0 text-red-600"
      strokeWidth={2.5}
      aria-hidden
    />
  );
}

function ReachMapHoverCard({
  point,
  accountData,
  selectedIds,
  toggleSelection,
  onClose,
}: {
  point: Point;
  accountData?: AccountMatrixData | null;
  selectedIds: Set<string>;
  toggleSelection: (id: string, additive: boolean) => void;
  onClose?: () => void;
}) {
  const meta = CLUSTER_META[point.cluster];
  const subtitle = reachMapSubtitle(point, accountData);
  const navigate = useNavigate();
  const { orgSlug } = useParams<{ orgSlug?: string }>();

  return (
    <div
      className="w-80 max-w-[calc(100vw-16px)] rounded-xl border border-slate-200 bg-white p-3 shadow-xl"
      onPointerDown={e => e.stopPropagation()}
    >
      <div className="flex gap-2.5">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-sky-100 text-xs font-bold text-sky-900">
          {initials(point.account_name)}
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-bold leading-snug text-slate-900">
            {point.account_name}
          </p>
          <p className="mt-0.5 truncate text-xs text-slate-500">{subtitle}</p>
        </div>
        {onClose ? (
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 rounded p-0.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
            aria-label="Close"
          >
            <X size={14} />
          </button>
        ) : null}
      </div>
      <div className="mt-3 flex flex-nowrap items-center gap-2">
        <span className="inline-flex shrink-0 items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-900">
          Account Health: {Math.round(point.overall)}
        </span>
        <span
          className="inline-flex min-w-0 max-w-full items-center truncate rounded-full px-2 py-0.5 text-xs font-semibold"
          style={{
            background: meta.bg,
            color: meta.text,
            border: `1px solid ${meta.color}44`,
          }}
        >
          {meta.label}
        </span>
      </div>
      <div className="mt-4 grid gap-2">
        <button
          type="button"
          disabled
          title="Coming soon"
          className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-left text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-400 disabled:opacity-70"
        >
          <ClipboardList className="h-4 w-4 shrink-0 text-slate-500" />
          View Daily Synopsis
        </button>
        <button
          type="button"
          onClick={() =>
            navigate(
              orgSlug
                ? `/${orgSlug}/account-workspace/${point.account_id}`
                : `/account-workspace/${point.account_id}`,
              {
                state: {
                  from: orgSlug
                    ? `/${orgSlug}/always-on-channel`
                    : "/always-on-channel",
                  fromLabel: "Always On Intelligence",
                },
              }
            )
          }
          className="flex items-center gap-2 rounded-lg bg-sky-100 px-3 py-2 text-left text-sm font-semibold text-sky-900 transition-colors hover:bg-sky-200"
        >
          <ArrowUpRight className="h-4 w-4 shrink-0" />
          Open Account Workspace
        </button>
        <button
          type="button"
          onClick={() => toggleSelection(point.account_id, true)}
          className="flex items-center gap-2 rounded-lg border border-violet-300 bg-violet-50 px-3 py-2 text-left text-sm font-semibold text-violet-700 transition-colors hover:bg-violet-100"
        >
          <Plus className="h-4 w-4 shrink-0" />
          {selectedIds.has(point.account_id)
            ? "Remove from Selection"
            : "Add to Selection"}
        </button>
      </div>
    </div>
  );
}

function RevenueMapClickPanel({
  point,
  accountData,
  selectedIds,
  toggleSelection,
  onClose,
}: {
  point: Point;
  accountData: AccountMatrixData | null | undefined;
  selectedIds: Set<string>;
  toggleSelection: (id: string, additive: boolean) => void;
  onClose: () => void;
}) {
  const meta = CLUSTER_META[point.cluster];
  const subtitle = revenueMapPanelSubtitle(accountData, meta.label);
  const arrLabel = formatArrRevenueBand(point.arrM);
  const nrrNum = Number.isFinite(point.nrr) ? Math.round(point.nrr) : null;
  const nrrStrong = nrrNum !== null && nrrNum >= 100;
  const trajectory = scoreTrajectoryLabel(point.overall);
  const trendColor =
    point.overall >= 75
      ? "text-emerald-700"
      : point.overall >= 50
        ? "text-amber-700"
        : "text-red-700";
  const navigate = useNavigate();
  const { orgSlug } = useParams<{ orgSlug?: string }>();

  return (
    <div className="space-y-2" onPointerDown={e => e.stopPropagation()}>
      <div className="flex items-start gap-2">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-emerald-100 text-[10px] font-bold text-emerald-900">
          {initials(point.account_name)}
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate pr-0.5 text-[12px] font-bold leading-snug text-slate-900">
            {point.account_name}
          </p>
          <p className="mt-0.5 truncate text-[11px] leading-snug text-slate-500">
            {subtitle}
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="-mr-0.5 -mt-px shrink-0 rounded p-0.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
          aria-label="Close"
        >
          <X size={14} />
        </button>
      </div>

      <div className="grid grid-cols-3 gap-1.5">
        <div className="rounded-md bg-slate-100 px-1.5 py-1.5">
          <p className="mb-px text-[9px] font-medium uppercase tracking-wide text-slate-500">
            ARR
          </p>
          <p className="mb-0 text-xs font-bold leading-tight text-slate-900">
            {arrLabel}
          </p>
        </div>
        <div className="rounded-md bg-slate-100 px-1.5 py-1.5">
          <p className="mb-px text-[9px] font-medium uppercase tracking-wide text-slate-500">
            NRR
          </p>
          <p
            className={`mb-0 text-xs font-bold leading-tight ${
              nrrStrong ? "text-emerald-600" : "text-slate-900"
            }`}
          >
            {nrrNum !== null ? `${nrrNum}%` : "—"}
          </p>
        </div>
        <div
          className="rounded-md px-1.5 py-1.5"
          style={{ background: meta.bg }}
        >
          <p
            className="mb-px text-[9px] font-medium uppercase tracking-wide opacity-90"
            style={{ color: meta.text }}
          >
            Account Health
          </p>
          <p
            className="mb-0 text-xs font-bold leading-tight"
            style={{ color: meta.text }}
          >
            {Math.round(point.overall)}
          </p>
        </div>
      </div>

      <div
        className={`flex flex-wrap items-center gap-1 text-[11px] font-semibold leading-none ${trendColor}`}
      >
        <ReachScoreTrendIcon overall={point.overall} />
        <span>{trajectory}</span>
        <span className="font-normal text-slate-400">·</span>
        <span>{arrLabel}</span>
      </div>

      <div className="grid gap-1.5 pt-px">
        <button
          type="button"
          disabled
          title="Coming soon"
          className="flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-2 py-1.5 text-left text-[11px] font-semibold text-slate-700 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-400 disabled:opacity-70"
        >
          <ClipboardList className="h-3.5 w-3.5 shrink-0 text-slate-500" />
          View Daily Synopsis
        </button>
        <button
          type="button"
          onClick={() =>
            navigate(
              orgSlug
                ? `/${orgSlug}/account-workspace/${point.account_id}`
                : `/account-workspace/${point.account_id}`,
              {
                state: {
                  from: orgSlug
                    ? `/${orgSlug}/always-on-channel`
                    : "/always-on-channel",
                  fromLabel: "Always On Intelligence",
                },
              }
            )
          }
          className="flex items-center gap-1.5 rounded-md bg-sky-100 px-2 py-1.5 text-left text-[11px] font-semibold text-sky-900 transition-colors hover:bg-sky-200"
        >
          <ArrowUpRight className="h-3.5 w-3.5 shrink-0" />
          Open Account Workspace
        </button>
        <button
          type="button"
          onClick={() => toggleSelection(point.account_id, true)}
          className="flex items-center gap-1.5 rounded-md border border-violet-300 bg-violet-50 px-2 py-1.5 text-left text-[11px] font-semibold text-violet-700 transition-colors hover:bg-violet-100"
        >
          <Plus className="h-3.5 w-3.5 shrink-0" />
          {selectedIds.has(point.account_id)
            ? "Remove from Selection"
            : "Add to Selection"}
        </button>
      </div>
    </div>
  );
}

export default BirdsEyeOverview;
